import React, { useEffect, useState } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { observer } from "mobx-react";
import clsx from 'clsx';
import { LeadDetails } from '../../leadDetails/leadDetails';
import { useStore } from '@roc/client-portal-shared/hooks';
import { Box, Button, Checkbox, FormControlLabel, Grid, Typography } from '@material-ui/core';
import { BorrowerDetailedScreen } from '../../borrowerDetails/borrowerDetailedScreen';
import { LenderDetailedScreen } from '../../lenderDetails/lenderDetailedScreen';
import { BORROWER_OBJECT_TYPE, LEAD_OBJECT_TYPE, TPO_OBJECT_TYPE, BROKER_OBJECT_TYPE, LEAD_CUSTOM_OBJECT_TYPE, BORROWER_CUSTOM_OBJECT_TYPE, TPO_CUSTOM_OBJECT_TYPE } from '../../leads/constants/constants';
import { Button as RocButton, Toolbar } from '@roc/ui';
import UnsubscribeModal from "./unsubscribeModal";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    peopleLayoutContainer: {
      display: 'flex',
      width: '100%',
      height: '80vh',
      border: '1px solid #ccc',
      overflow: 'hidden',
    },
    peopleList: {
      width: '18%',
      borderRight: '1px solid #ccc',
      overflowY: 'auto',
      margin: 0,
      padding: 0,
      position: 'relative',
      height: '100%',
    },
    peopleListH3: {
      marginLeft: '10px',
      position: 'sticky',
      top: 0,
      backgroundColor: '#fff',
      zIndex: 1,
      padding: '10px 0',
    },
    peopleListUl: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    peopleListLi: {
      padding: '10px',
      cursor: 'pointer',
      borderBottom: '1px solid #eee',
      display: 'flex',
      alignItems: 'center',
      '&:hover': {
        backgroundColor: '#f9f9f9',
      },
    },
    peopleListLiSelected: {
      backgroundColor: '#ddd',
    },
    peopleDetail: {
      flex: 1,
      padding: '20px',
      overflowY: 'auto',
      height: '100%',
      maxHeight: '80vh',
    },
    completedButtonContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px',
      borderBottom: '1px solid #ccc',
      position: 'sticky',
      top: 0,
      backgroundColor: '#fff',
      zIndex: 1,
    },
    button: {
      marginLeft: theme.spacing(2),
      minWidth: '100px',
    },
    noSelectionMessage: {
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: '#666',
      fontSize: '1.1rem',
    },
  })
);

export const CallStepsDue = observer(({ currentStep }) => {
  const classes = useStyles();
  const [selectedPerson, setSelectedPerson] = useState<any>(null);
  const [listOfPropspects, setListOfPropspects] = useState<any>([]);
  const { salesforceLeadStore, cadenceStore } = useStore();
  const [selectedProspectIds, setSelectedProspectIds] = useState(new Set());
  const [unsubscribeOpen, setUnsubscribeOpen] = useState(false);

  useEffect(() => {
    setListOfPropspects(currentStep?.prospects ?? [])
  }, [currentStep]);

  const handleCheckboxChange = (personId) => (event) => {
    setSelectedProspectIds((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (event.target.checked) {
        newSelected.add(personId);
      } else {
        newSelected.delete(personId);
      }
      return newSelected;
    });
  };

  const handleMarkCompleted = () => {
    const idsToComplete = Array.from(selectedProspectIds);
    onMarkCompleted(idsToComplete);
    setSelectedProspectIds(new Set());
    setSelectedPerson(null);
  };

  const handleMarkUnsubscribed = (reason?: string) => {
    const idsToUnsubscribe = Array.from(selectedProspectIds);
    onMarkUnsubscribed(idsToUnsubscribe, reason);
    setSelectedProspectIds(new Set());
    setSelectedPerson(null);
  };

  const onMarkCompleted = (listOfPeople: any) => {
    cadenceStore.completeCallStepForCadenceContacts(listOfPeople);
  };

  const onMarkUnsubscribed = (listOfPeople: any, reason?: string) => {
    cadenceStore.markCadenceContactAsUnsubscribed(listOfPeople, reason || "");
  };

  const handleSelectPerson = (person: any) => {
    const personId = person.cadenceContactInfo?.cadencesContactDataId;

    setSelectedPerson({
      ...person,
      modalDisplayInfo: getCorrectDisplayInfoModal(person)
    });

    setSelectedProspectIds((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(personId)) {
        newSelected.delete(personId);
      } else {
        newSelected.clear();
        newSelected.add(personId);
      }
      return newSelected;
    });
  };

  const getCorrectId = data => {
    if (data?.cadenceContactInfo?.leadId) {
      return data?.cadenceContactInfo?.leadId;
    } else if (data?.cadenceContactInfo?.contactId) {
      return data?.cadenceContactInfo?.contactId;
    }
  };

  const getCorrectDisplayInfoModal = data => {
    if (data?.recordType.toUpperCase() === LEAD_OBJECT_TYPE) {
      return {
        id: data?.cadenceContactInfo?.leadId,
        recordType: LEAD_OBJECT_TYPE
      };
    } else if (data?.recordType.toUpperCase() === BORROWER_OBJECT_TYPE) {
      return {
        id: data?.cadenceContactInfo?.contactId,
        recordType: BORROWER_OBJECT_TYPE,
        entityId:data?.cadenceContactInfo?.entityId
      };
    } else if (data?.recordType.toUpperCase() === TPO_OBJECT_TYPE || data?.recordType.toUpperCase() === BROKER_OBJECT_TYPE) {
      return {
        id: data?.cadenceContactInfo?.contactId,
        recordType: data?.recordType,
        entityId:data?.cadenceContactInfo?.entityId
      }
    }
  };

  const renderDetails = () => {
    if (!selectedPerson) return null;

    const { recordType } = selectedPerson.modalDisplayInfo;
    const { id, entityId } = selectedPerson.modalDisplayInfo;

    switch (recordType) {
      case LEAD_OBJECT_TYPE:
        return (
          <LeadDetails
            key={id}
            salesforceLeadStore={salesforceLeadStore}
            id={id}
            fromCadence={true}
          />
        );
      case BORROWER_OBJECT_TYPE:
        return <BorrowerDetailedScreen key={entityId} id={entityId} fromCadence={true} />;
      case (TPO_OBJECT_TYPE || BROKER_OBJECT_TYPE):
        return <LenderDetailedScreen key={entityId} id={entityId} fromCadence={true} contactRecordType={recordType} />;
      default:
        return <Box>Unknown type</Box>;
    }
  };
  return (
    <>
      <Toolbar toolbarContainerStyle={{ padding: '10px' }}>
        <Grid container spacing={2}>
          <Grid item>
            <RocButton
              className={null}
              variant="contained"
              size="small"
              onClick={() => setUnsubscribeOpen(true)}
              color="primary"
              testId="start-dial-session"
              disabled={selectedProspectIds.size === 0}
            >
              Unsubscribe
            </RocButton>
          </Grid>
          <Grid item>
            <RocButton
              className={null}
              variant="contained"
              size="small"
              onClick={() => handleMarkCompleted()}
              color="primary"
              testId="start-dial-session"
              disabled={selectedProspectIds.size === 0}
            >
              Mark completed
            </RocButton>
          </Grid>
        </Grid>
      </Toolbar>

      <Box className={classes.peopleLayoutContainer}>
        <Box className={classes.peopleList}>
          <Box className={classes.completedButtonContainer}>
            <h3 className={classes.peopleListH3}>Prospects</h3>
          </Box>
          <ul className={classes.peopleListUl}>
            {listOfPropspects.map((person) => {
              const personId = getCorrectId(person);
              const isSelected = selectedProspectIds.has(person.cadenceContactInfo?.cadencesContactDataId);
              return (
                <li
                  key={personId}
                  className={clsx(classes.peopleListLi, {
                    [classes.peopleListLiSelected]: selectedPerson?.modalDisplayInfo?.id === personId,
                  })}
                  onClick={() => handleSelectPerson(person)}
                >

                  <Checkbox
                    checked={isSelected}
                    onChange={handleCheckboxChange(person.cadenceContactInfo?.cadencesContactDataId)}
                    name={`checkbox-${personId}`}
                    color="primary"
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                  />

                  <Typography>
                    {`${person?.firstName} ${person?.lastName}`}
                  </Typography>

                </li>
              );
            })}
          </ul>
        </Box>

        <Box className={classes.peopleDetail}>
          {selectedPerson ? (
            renderDetails()
          ) : (
            <Box className={classes.noSelectionMessage}>
              Select a prospect to see details
            </Box>
          )}
        </Box>
      </Box>

      <UnsubscribeModal
        open={unsubscribeOpen}
        onClose={() => setUnsubscribeOpen(false)}
        onConfirm={(reason) => handleMarkUnsubscribed(reason)}
      />
    </>
  );
});
